import { AttendanceEntriesCounts, JustificationCounts } from '@schooly/api';
import { FC, useMemo } from 'react';

import { useAttendanceCodes } from '../../../hooks/useAttendanceCodes';
import { getPresentAbsentCodeIds } from '../attendance-utils';
import {
  AttendanceStatsSummaryCards,
  calculatePresentTrackedTotals,
  roundTo,
} from './AttendanceGroupStatsSummary';
import { PERCENT_PRECISION } from './common/constants';

/** Present% is truncated per spec: 180 / 192 = 93.75 -> 93.7, Absent% = 100 - Present%. */
const floorTo = (value: number, precision: number): number => {
  const factor = 10 ** precision;
  return Math.floor(value * factor) / factor;
};

type AttendanceStudentStatsSummaryProps = {
  counts: AttendanceEntriesCounts[];
  justificationCounts?: JustificationCounts;
  loading?: boolean;
};

export const AttendanceStudentStatsSummary: FC<AttendanceStudentStatsSummaryProps> = ({
  counts,
  justificationCounts,
  loading,
}) => {
  const { attendanceCodes } = useAttendanceCodes();

  const stats = useMemo(() => {
    const presentCodeIds = new Set(getPresentAbsentCodeIds(attendanceCodes).present);
    const { totalPresent, totalTracked } = calculatePresentTrackedTotals(counts, presentCodeIds);

    if (!totalTracked) {
      return { presentPercent: 0, absentPercent: 0 };
    }

    const presentPercent = floorTo((totalPresent / totalTracked) * 100, PERCENT_PRECISION);
    return {
      presentPercent,
      absentPercent: roundTo(100 - presentPercent, PERCENT_PRECISION),
    };
  }, [counts, attendanceCodes]);

  return (
    <AttendanceStatsSummaryCards
      loading={loading}
      showStats={counts.length > 0}
      presentPercent={stats.presentPercent}
      absentPercent={stats.absentPercent}
      justificationCounts={justificationCounts}
    />
  );
};
